import React, { createContext, useContext, useCallback, useState } from 'react';

export interface PanelRecord {
  id: string | number;
  /** User-facing identifier; defaults to the internal record ID. */
  displayId?: string | number;
  table: string;       // route name (URL segment)
  tableName: string;    // actual table name in DB
  label: string;        // display label for the tab
}

interface SidePanelState {
  /** Ordered list of open records (tabs) */
  openRecords: PanelRecord[];
  /** Currently active record ID + table (composite key) */
  activeKey: string | null;
  /** Whether the panel is visible or tucked away */
  visible: boolean;
  /** Currently selected inner tab */
  innerTab: string;
}

interface SidePanelContextValue extends SidePanelState {
  /** Open a record in the panel. Adds a tab if not already open, activates it. */
  openRecord: (record: PanelRecord) => void;
  /** Close a specific tab */
  closeTab: (key: string) => void;
  /** Switch to a tab */
  activateTab: (key: string) => void;
  /** Reorder tabs via drag */
  reorderTabs: (fromIdx: number, toIdx: number) => void;
  /** Toggle panel visibility (hide/show) */
  togglePanel: () => void;
  /** Hide the panel */
  hidePanel: () => void;
  /** Show the panel */
  showPanel: () => void;
  /** Set the inner tab */
  setInnerTab: (tab: string) => void;
  /** Close all tabs */
  closeAllTabs: () => void;
  /** Close all tabs except the given key */
  closeOtherTabs: (key: string) => void;
  /** Close tabs to the right of the given key */
  closeTabsToRight: (key: string) => void;
  /** Get the active record */
  activeRecord: PanelRecord | null;
}

const MAX_TABS = 6;

/** Composite key for a record: table::id */
export function makeKey(table: string, id: string | number): string {
  return `${table}::${id}`;
}


const SidePanelContext = createContext<SidePanelContextValue | null>(null);
const OpenRecordContext = createContext<((record: PanelRecord) => void) | null>(null);

export function SidePanelProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SidePanelState>({
    openRecords: [],
    activeKey: null,
    visible: false,
    innerTab: 'Submissions',
  });

  const openRecord = useCallback((record: PanelRecord) => {
    // The persistent side panel is reserved for merchants. Routing to the right
    // presentation is `useRecordNavigator`'s job — reaching here with anything
    // else means a caller bypassed it.
    if (record.tableName !== 'merchant') {
      console.error(`SidePanel: refusing to host non-merchant record "${record.tableName}". Use useRecordNavigator().openRecord.`);
      return;
    }

    const key = makeKey(record.table, record.id);
    setState(prev => {
      let records = [...prev.openRecords];
      const exists = records.some(r => makeKey(r.table, r.id) === key);

      if (!exists) {
        records.push(record);
        // Cap at MAX_TABS — drop oldest non-active
        if (records.length > MAX_TABS) {
          const activeKey = key; // new one is about to become active
          const dropIdx = records.findIndex(r => makeKey(r.table, r.id) !== activeKey);
          if (dropIdx >= 0) records.splice(dropIdx, 1);
        }
      }

      return {
        ...prev,
        openRecords: records,
        activeKey: key,
        visible: true,
      };
    });
  }, []);

  const closeTab = useCallback((key: string) => {
    setState(prev => {
      const records = prev.openRecords.filter(r => makeKey(r.table, r.id) !== key);
      let activeKey = prev.activeKey;

      if (prev.activeKey === key) {
        // Activate the last remaining tab, or null
        activeKey = records.length > 0
          ? makeKey(records[records.length - 1].table, records[records.length - 1].id)
          : null;
      }

      return {
        ...prev,
        openRecords: records,
        activeKey,
        visible: records.length > 0 ? prev.visible : false,
      };
    });
  }, []);

  const activateTab = useCallback((key: string) => {
    setState(prev => ({ ...prev, activeKey: key }));
  }, []);

  const reorderTabs = useCallback((fromIdx: number, toIdx: number) => {
    setState(prev => {
      const records = [...prev.openRecords];
      const [moved] = records.splice(fromIdx, 1);
      records.splice(toIdx, 0, moved);
      return { ...prev, openRecords: records };
    });
  }, []);

  const togglePanel = useCallback(() => {
    setState(prev => ({ ...prev, visible: !prev.visible }));
  }, []);

  const hidePanel = useCallback(() => {
    setState(prev => ({ ...prev, visible: false }));
  }, []);

  const showPanel = useCallback(() => {
    setState(prev => ({ ...prev, visible: prev.openRecords.length > 0 ? true : prev.visible }));
  }, []);

  const setInnerTab = useCallback((tab: string) => {
    setState(prev => ({ ...prev, innerTab: tab }));
  }, []);

  const closeAllTabs = useCallback(() => {
    setState(prev => ({ ...prev, openRecords: [], activeKey: null, visible: false }));
  }, []);

  const closeOtherTabs = useCallback((key: string) => {
    setState(prev => {
      const kept = prev.openRecords.filter(r => makeKey(r.table, r.id) === key);
      return { ...prev, openRecords: kept, activeKey: kept.length > 0 ? key : null };
    });
  }, []);

  const closeTabsToRight = useCallback((key: string) => {
    setState(prev => {
      const idx = prev.openRecords.findIndex(r => makeKey(r.table, r.id) === key);
      if (idx < 0) return prev;
      const kept = prev.openRecords.slice(0, idx + 1);
      const newActive = kept.some(r => makeKey(r.table, r.id) === prev.activeKey)
        ? prev.activeKey
        : key;
      return { ...prev, openRecords: kept, activeKey: newActive };
    });
  }, []);

  const activeRecord = state.activeKey
    ? state.openRecords.find(r => makeKey(r.table, r.id) === state.activeKey) || null
    : null;

  const value: SidePanelContextValue = {
    ...state,
    openRecord,
    closeTab,
    activateTab,
    reorderTabs,
    togglePanel,
    hidePanel,
    showPanel,
    setInnerTab,
    closeAllTabs,
    closeOtherTabs,
    closeTabsToRight,
    activeRecord,
  };

  return (
    <OpenRecordContext.Provider value={openRecord}>
      <SidePanelContext.Provider value={value}>
        {children}
      </SidePanelContext.Provider>
    </OpenRecordContext.Provider>
  );
}

export function useSidePanel(): SidePanelContextValue {
  const ctx = useContext(SidePanelContext);
  if (!ctx) throw new Error('useSidePanel must be used within SidePanelProvider');
  return ctx;
}

/**
 * Subscribe only to the stable open action, not mutable panel state.
 *
 * Low-level: puts a merchant in the persistent panel unconditionally. Feature
 * code should call `useRecordNavigator().openRecord`, which picks the right
 * presentation for the table.
 */
export function useOpenRecordInPanel(): (record: PanelRecord) => void {
  const openRecord = useContext(OpenRecordContext);
  if (!openRecord) throw new Error('useOpenRecordInPanel must be used within SidePanelProvider');
  return openRecord;
}
